<template>
  <div>
    <transition name="fade">
      <div v-if="visible" class="overlay" @click.self="close"></div>
    </transition>
    <transition name="slide">
      <div v-if="visible" class="base-modal__overlay">
        <div class="base-modal__container" :style="{ width: modalWidth }">
          <!-- HEADER -->
          <header class="base-modal__header">
            <h2 class="base-modal__title">{{ title }}</h2>
            <button v-if="showCloseButton" class="base-modal__close-btn" @click="close">
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
          </header>

          <!-- SLOT FOR ARBITRARY CONTENT -->
          <section class="base-modal__body">
            <slot />
          </section>

          <!-- FOOTER: always two buttons: Submit (blue) + Cancel (red) -->
          <footer class="base-modal__footer" v-if="submitShow || cancelShow">
            <button
                class="submit-button cardBackground"
                :style="{ width: submitWidth, backgroundColor: submitColor, borderColor: submitBorderColor }"
                @click="$emit('submit')"
                v-if="submitShow"
                :disabled="submitDisabled"
            >
              {{ submitLabel }}
            </button>
            <button
                class="submit-button cardBackground"
                :style="{ width: secondaryWidth, backgroundColor: secondaryColor, borderColor: secondaryBorderColor }"
                @click="$emit('secondary')"
                v-if="secondaryShow"
            >
              {{ secondaryLabel }}
            </button>
            <button
                class="cancel-button cardBackground"
                :style="{ width: cancelWidth, backgroundColor: cancelColor, borderColor: cancelBorderColor }"
                @click="$emit('cancel')"
                v-if="cancelShow"
            >
              {{ cancelLabel }}
            </button>
          </footer>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'BaseModal',

  props: {
    /**
     * When true, modal is visible.
     */
    visible: {
      type: Boolean,
      required: true
    },

    /**
     * Title text at top of modal.
     */
    title: {
      type: String,
      default: 'Base Modal'
    },

    /**
     * Width of the modal container, e.g. "20rem", "30rem", etc.
     */
    modalWidth: {
      type: String,
      default: '85%'
    },

    /**
     * The label for the submit button.
     */
    submitLabel: {
      type: String,
      default: 'Submit'
    },

    /**
     * Overall width of the submit button, e.g. "6rem", "8rem", etc.
     */
    submitWidth: {
      type: String,
      default: '6rem'
    },

    /**
     * Boolean to show submit button, e.g. true, false, etc.
     */
    submitShow: {
      type: Boolean,
      default: true
    },

    /**
     * Color of submit button, e.g. true, false, etc.
     */
    submitColor: {
      type: String,
      default: 'var(--color-secondary-button)'
    },
    submitBorderColor: {
      type: String,
      default: 'var(--color-secondary-button-border)'
    },

    submitDisabled: {
      type: Boolean,
      default: false,
    },

    /**
     * The label for the cancel button.
     */
    cancelLabel: {
      type: String,
      default: 'Cancel'
    },

    /**
     * Overall width of the cancel button, e.g. "6rem", "8rem", etc.
     */
    cancelWidth: {
      type: String,
      default: '6rem'
    },

    /**
     * Boolean to show cancel button, e.g. true, false, etc.
     */
    cancelShow: {
      type: Boolean,
      default: true
    },

    /**
     * Color of submit button, e.g. true, false, etc.
     */
    cancelColor: {
      type: String,
      default: 'var(--color-red-button)'
    },
    cancelBorderColor: {
      type: String,
      default: 'var(--color-red-button-border)'
    },

    /**
     * The label for the secondary button.
     */
    secondaryLabel: {
      type: String,
      default: 'Submit'
    },

    /**
     * Overall width of the submit button, e.g. "6rem", "8rem", etc.
     */
    secondaryWidth: {
      type: String,
      default: '6rem'
    },

    /**
     * Boolean to show secondary button, e.g. true, false, etc.
     */
    secondaryShow: {
      type: Boolean,
      default: false
    },

    /**
     * Color of submit button, e.g. true, false, etc.
     */
    secondaryColor: {
      type: String,
      default: 'var(--color-secondary-button)'
    },
    secondaryBorderColor: {
      type: String,
      default: 'var(--color-secondary-button-border)'
    },

    /**
     * Boolean to show close button (X) in header, e.g. true, false, etc.
     */
    showCloseButton: {
      type: Boolean,
      default: true
    },
  },

  methods: {
    /**
     * Emit `close` so parent can set visible = false.
     */
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
/* Overlay dims the background */
.base-modal__overlay {
  position: fixed;
  inset: 0;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

/* Modal container */
.base-modal__container {
  background-color: var(--color-modal-color);
  border: 2px solid var(--color-modal-color);
  border-radius: 0.75rem;
  box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  margin-bottom: 10vh;
  overflow: visible;
  position: relative;
  overflow-y: auto; /* scroll the body if it’s too tall */
}

/* Header bar */
.base-modal__header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 1rem;
  padding-top: 1rem;
  padding-bottom: .25rem;
  background: var(--color-modal-color);
}
.base-modal__header::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 5%;
  width: 90%;
  height: 1px;
  background-color: var(--color-modal-divider);
}

/* Title text */
.base-modal__title {
  margin: 0;
  font-size: 1.875rem;
  color: var(--text-primary);
  font-family: 'Oxanium', 'Inter', sans-serif;
  font-weight: 300;
  text-transform: uppercase;
  flex: 1;
  text-align: left;
  padding-right: 8rem;
  padding-top: .5rem;
}

/* Close button in header */
.base-modal__close-btn {
  background: none;
  border: none;
  font-size: 1.9rem;
  color: var(--text-primary);
  cursor: pointer;
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 10;
}

/* Body slot area */
.base-modal__body {
  padding: 1rem;
  background: var(--color-modal-color);
  color: var(--text-primary);
  font-size: 1rem;
  overflow-y: auto;
  max-height: 50vh !important;
}

/* Footer */
.base-modal__footer {
  position: relative;
  padding: 1rem 1rem 1.5rem;
  background: var(--color-modal-color);
  display: flex;
  justify-content: space-evenly;
}

/* Buttons */
.submit-button {
  background: var(--color-secondary-button);
  border: 1px solid var(--color-secondary-button-border);
  color: var(--text-primary);
  font-size: 1.25rem;
  border-radius: 0.25rem;
  padding: 0.5rem;
  font-family: 'Helvetica', sans-serif;
  cursor: pointer;
}

.cancel-button {
  background-color: var(--error-red);
  border: 1px solid var(--error-red);
  color: var(--text-primary);
  font-size: 1.25rem;
  border-radius: 0.25rem;
  padding: 0.5rem;
  font-family: 'Helvetica', sans-serif;
  cursor: pointer;
}

/* slide-in/out animation */
.slide-enter-active,
.slide-leave-active {
  transition: all .3s ease;
}
.slide-enter {
  transform: translateY(100%);
  opacity: 0;
}
.slide-enter-to {
  transform: translateY(0);
  opacity: 1;
}
.slide-leave {
  transform: translateY(0);
  opacity: 1;
}
.slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* fade-in/out animation */
.fade-enter-active,
.fade-leave-active {
  transition: opacity .3s ease;
}
.fade-enter {
  opacity: 0;
  pointer-events: none;
}
.fade-enter-to {
  opacity: 1;
  pointer-events: auto;
}
.fade-leave {
  opacity: 1;
  pointer-events: auto;
}
.fade-leave-to {
  opacity: 0;
  pointer-events: none;
}
</style>
